import { captureException } from "@storytell/analytics";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { SplashScreenFallback } from "./SplashScreenFallback";

// A stalled lazy route chunk pins the root Suspense forever with no error, so
// ErrorBoundary never fires and the user is stuck on the boot splash. A refresh
// re-fetches the chunk and recovers. This fallback automates that refresh once,
// then exposes a manual button so a persistent failure doesn't reload-loop.
const ROOT_STUCK_MS = 10_000;

// One auto-reload per boot attempt. sessionStorage survives the reload but
// clears on tab close, so a genuinely broken build degrades to the manual
// button instead of looping.
const RELOAD_GUARD_KEY = "root-suspense-auto-reloaded";

export const RootSuspenseFallback = () => {
  const [stuck, setStuck] = createSignal(false);

  onMount(() => {
    if (isServer) return;

    const timer = setTimeout(() => {
      setStuck(true);
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
      } catch {
        // sessionStorage unavailable: skip the auto-reload — without the guard it would loop.
        alreadyReloaded = true;
      }

      // Measure how often boot actually stalls past the threshold and whether
      // the one-shot auto-reload fired, so we can tell if this net is dead
      // weight (rarely fires) or pointing at a real chunk-serving problem.
      captureException(new Error("Root Suspense stalled past threshold"), {
        errorType: "RootSuspenseStall",
        autoReloaded: !alreadyReloaded,
        thresholdMs: ROOT_STUCK_MS,
      });

      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
        } catch {
          // ignore
        }
        window.location.reload();
      }
    }, ROOT_STUCK_MS);

    onCleanup(() => clearTimeout(timer));
  });

  // English is inlined: this renders as the root Suspense fallback, which is
  // mounted outside I18nProvider, so useI18n() would throw here (and on SSR).
  return (
    <>
      <SplashScreenFallback />
      <Show when={stuck()}>
        <div class="fixed inset-x-0 bottom-16 z-9999 flex flex-col items-center gap-3">
          <p class="text-sm text-secondary-text">This is taking longer than expected.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            class="rounded-lg bg-brand px-4 py-1.5 text-sm text-on-brand transition-colors hover:bg-brand-hover"
          >
            Reload
          </button>
        </div>
      </Show>
    </>
  );
};
